"use client";

import Link from "next/link";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { rankTier } from "@/lib/keyword-rank-tiers";
import { byRankThenKeyword, historyNote } from "@/lib/keyword-explorer";
import { difficultyTitle } from "@/lib/keywords/keyword-scores";
import { KeywordMovement } from "@/components/keywords/keyword-movement";
import {
  DIFFICULTY_FOOTNOTE,
  DifficultyBar,
  KeywordErrorNote,
  RankPill,
} from "@/components/keywords/keyword-cells";
import { useKeywordRanks } from "@/hooks/use-keyword-ranks";
import { useKwPanelFlag } from "@/hooks/use-kw-panel-flag";
import { useThemeConfig } from "@/hooks/use-theme-config";

/**
 * App Store search positions for one app.
 *
 * Four columns and no more: Rank, Keyword, Change, Difficulty. Popularity and
 * Apple's own figure are deliberately NOT here. This card is about a third of a
 * 1440 viewport wide and six columns overflow it, so the two extra scores live on
 * the keyword page, which has the width for them. The route still reads both;
 * only this card declines to draw them.
 *
 * It renders in the right-hand column under the globe on a wide screen and in
 * the main column below it, so it is sized for roughly a third of the viewport
 * rather than the full width it used to have.
 *
 * It hides itself: nothing is drawn while loading or when the route answers with
 * zero rows, because nearly every app has no positions and an empty card only
 * raised questions (owner 2026-09-17). A failed fetch still draws the card with
 * its error note, so a broken route never reads as "no positions".
 *
 * ponytail: ui/table, not RankingTable. RankingTable is built for app rows and
 * builds its React key from app_store_id plus country_code, which collapses to
 * one identical key for every row of a single-app panel, and it prefetches on
 * row hover. Two workarounds to borrow a component is a bigger change than this
 * file. Revisit if this ever needs sorting or a change column.
 *
 * ponytail: the rank chip and the header pill are spans styled here, not
 * ui/badge. app-ads.tsx renders its header count and its per-ad chips exactly
 * this way one section above, so this matches its neighbour. ui/badge would
 * also fight us: its terminal styles are `!important` and `cn` is tailwind-merge,
 * so a per-tier className passed in would win over them and leak amber into the
 * terminal theme.
 */
export function AppKeywordRanks({
  appId,
  className,
  variant = "section",
}: {
  appId: number;
  className?: string;
  /**
   * "section" is the main column, where this carries the same large heading as
   * its neighbours there.
   *
   * "card" is the right-hand column from lg up, where it is the third card
   * beside Competitors and Country Rankings and has to carry THEIR border
   * instead. Same data and same table either way, only the chrome differs.
   */
  variant?: "section" | "card";
}) {
  // Behind a flag while under review: hidden, and nothing is fetched (the hook
  // is off for appId 0), unless one of our own accounts switched it on in the left menu.
  const { visible } = useKwPanelFlag();
  // 14 days: the history note under the table counts the days observed. The
  // Change column is still the last two days of it.
  const { items, observedDays, unavailable, isLoading } =
    useKeywordRanks(visible ? appId : 0, 14);
  const { isTerminal, mounted } = useThemeConfig();
  const terminal = mounted && isTerminal;

  if (!visible) return null;
  if (isLoading || (!unavailable && items.length === 0)) return null;

  // One market for the whole payload, so it is said once in the header badge
  // instead of repeated on every row.
  const market = items[0]?.country_code.toUpperCase() ?? "US";

  // app-ads.tsx styles its header pills this way one section above, so the
  // source label reads as part of the same family.
  const pill = cn(
    "inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold leading-none",
    terminal
      ? "font-mono text-emerald-300 ring-1 ring-emerald-500/40"
      : "text-foreground/80 ring-1 ring-border bg-muted/40",
  );

  const rows = byRankThenKeyword(items.map((k) => ({ ...k, rank: k.rank_position })));

  // One line that says what a row IS: this app's place in the search results
  // for that keyword. "2 keywords rank" was read off the card as a puzzle
  // (feedback 2026-09-16 item 4).
  const subLine = unavailable
    ? "not available"
    : `Where this app ranks in App Store search, for ${rows.length} keyword${rows.length === 1 ? "" : "s"}`;

  // When every keyword on the panel has been swept once, the Change column can
  // only be dashes, and a sentence saying why beats an unexplained empty
  // column. It is the state a newly added keyword starts in. No other footnote:
  // the owner read the difficulty explainer off this card and asked for it gone.
  const notes = observedDays.length === 1 ? (
    <p className={cn("mt-3 px-2 text-[11px] leading-snug text-muted-foreground", terminal && "font-mono")}>
      {historyNote(observedDays)}
    </p>
  ) : null;

  const body = unavailable ? (
    // No `onRetry`: see `KeywordErrorNote`.
    <KeywordErrorNote what="positions" className={cn(terminal && "font-mono")} />
  ) : (
    <>
      {/* Under sm the table is fixed to the screen instead of min-w-max: the
          four columns wanted 543px at 375 and Difficulty was cut at the edge.
          Change moves under the keyword there, so Rank, Keyword and Difficulty
          share the width and the keyword truncates. */}
      <Table className="max-sm:min-w-0 max-sm:table-fixed">
        <TableHeader>
          <TableRow>
            {/* Wide enough for the chip at its three-digit width: positions run to #188. */}
            <TableHead className="w-[80px] max-sm:w-[72px]">Rank</TableHead>
            <TableHead>Keyword</TableHead>
            {/* Narrow on purpose: the widest thing it holds is "no change". The
                period is per row (a keyword can miss a day), so the cell's own
                title names the date; the header says only what the number is. */}
            <TableHead className="hidden w-[76px] sm:table-cell" title="Places moved since this keyword was last checked">
              Change
            </TableHead>
            {/* A popover, not a title: it opens on a tap as well as a click,
                and the header is the one place the score is explained now that
                the explainer paragraphs are gone from the card. */}
            <TableHead className="w-[104px] max-sm:w-[84px]">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex cursor-help items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="What Difficulty means"
                  >
                    Difficulty
                    <Info className="size-3 shrink-0" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 space-y-1.5 p-3 text-xs">
                  <p className="font-medium">
                    How hard it is to reach the first page of App Store search for this keyword.
                  </p>
                  <p>{DIFFICULTY_FOOTNOTE}</p>
                  <p className={cn(!terminal && "text-muted-foreground")}>
                    Not Apple&apos;s figure, and not comparable to a vendor&apos;s.
                  </p>
                </PopoverContent>
              </Popover>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((k) => {
            const movement = (
              <KeywordMovement
                state={k.movement_state}
                delta={k.movement}
                previousRank={k.previous_rank}
                previousOn={k.previous_on}
                terminal={terminal}
              />
            );
            return (
              <TableRow key={k.keyword_id}>
                <TableCell>
                  <RankPill
                    rank={k.rank_position}
                    terminal={terminal}
                    title={`${rankTier(k.rank_position).label} — position ${k.rank_position} in ${market} App Store search for “${k.keyword}”`}
                  />
                </TableCell>
                <TableCell className="font-medium">
                  {/* The keyword is the subject of its own page, so it links to
                      it. keyword_id is the primary key of kw_keywords and that
                      table carries country_code, so the id alone IS the (keyword,
                      country) pair and a second param could only disagree with
                      it. */}
                  <Link
                    href={`/keywords?k=${k.keyword_id}`}
                    className="block truncate hover:underline"
                    title={`See which apps rank for “${k.keyword}”, day by day`}
                  >
                    {k.keyword}
                  </Link>
                  <span className="block sm:hidden">{movement}</span>
                </TableCell>
                <TableCell className="hidden sm:table-cell">{movement}</TableCell>
                <TableCell>
                  <DifficultyBar
                    score={k.difficulty?.score ?? null}
                    title={k.difficulty ? difficultyTitle(k.difficulty, k.keyword) : undefined}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {notes}
    </>
  );

  // The card. It scrolls with the sidebar region that mounts it, never on its
  // own; the bottom border hands over to the Country Rankings header below it.
  const wrapper = variant === "card"
    ? cn(
        "w-full shrink-0",
        terminal ? "border-b-2 border-emerald-700/50 bg-black" : "border-b border-sidebar-border",
        className,
      )
    : cn("w-full", className);

  return (
    <section className={wrapper} aria-label="App Store search positions">
      <div className={cn("mb-3 flex items-start justify-between gap-2", variant === "card" && "px-2 pt-2")}>
        <div className="min-w-0">
          <h2
            className={cn(
              "font-bold leading-none tracking-tight",
              variant === "card" ? "text-[13px]" : "text-xl",
            )}
          >
            Search positions
          </h2>
          <p className={cn("mt-1 text-[11.5px] leading-snug text-muted-foreground", terminal && "font-mono")}>
            {subLine}
          </p>
        </div>
        <span className={pill} title="App Store search is the only source collected today">
          App Store · {market}
        </span>
      </div>
      {body}
    </section>
  );
}
